import React from "react";

interface AdditionalStatsProps {
  weeklyStats: {
    weeklyCredits: number;
    weeklyDebits: number;
    weeklyCount: number;
  };
  monthlyStats: {
    monthlyCredits: number;
    monthlyDebits: number;
    monthlyCount: number;
  };
  averageTransaction: number;
}

const AdditionalStats: React.FC<AdditionalStatsProps> = ({
  weeklyStats,
  monthlyStats,
  averageTransaction,
}) => {
  const stats = [
    {
      period: "This Week",
      credits: weeklyStats.weeklyCredits,
      debits: weeklyStats.weeklyDebits,
      count: weeklyStats.weeklyCount,
      gradient: "from-blue-400 to-cyan-500",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-100",
    },
    {
      period: "This Month",
      credits: monthlyStats.monthlyCredits,
      debits: monthlyStats.monthlyDebits,
      count: monthlyStats.monthlyCount,
      gradient: "from-emerald-400 to-green-500",
      bgColor: "bg-emerald-50",
      borderColor: "border-emerald-100",
    },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
      {stats.map((stat, index) => (
        <div
          key={stat.period}
          className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 relative overflow-hidden"
        >
          <div
            className={`absolute top-0 right-0 w-24 h-24 bg-gradient-to-br ${stat.gradient} opacity-10 rounded-bl-full`}
          ></div>
          <div className="relative">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {stat.period}
            </h3>
            <div className="grid grid-cols-3 gap-3">
              <div
                className={`text-center p-3 ${stat.bgColor} rounded-lg ${stat.borderColor} border`}
              >
                <div className="text-lg font-bold text-green-600">
                  +₹
                  {stat.credits.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                  })}
                </div>
                <div className="text-xs text-green-600 font-medium">
                  Credits
                </div>
              </div>
              <div
                className={`text-center p-3 ${stat.bgColor} rounded-lg ${stat.borderColor} border`}
              >
                <div className="text-lg font-bold text-red-600">
                  -₹
                  {stat.debits.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                  })}
                </div>
                <div className="text-xs text-red-600 font-medium">Debits</div>
              </div>
              <div
                className={`text-center p-3 ${stat.bgColor} rounded-lg ${stat.borderColor} border`}
              >
                <div className="text-lg font-bold text-blue-600">
                  {stat.count}
                </div>
                <div className="text-xs text-blue-600 font-medium">Count</div>
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-gray-100">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Net Flow</span>
                <span
                  className={`text-sm font-semibold ${
                    stat.credits - stat.debits >= 0
                      ? "text-green-600"
                      : "text-red-600"
                  }`}
                >
                  {stat.credits - stat.debits >= 0 ? "+" : ""}₹
                  {(stat.credits - stat.debits).toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                  })}
                </span>
              </div>
            </div>
          </div>
        </div>
      ))}

      {/* Average Transaction Card */}
      <div className="lg:col-span-2">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 relative overflow-hidden">
          <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-br from-orange-400 to-red-500 opacity-10 rounded-bl-full"></div>
          <div className="relative">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Transaction Insights
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center p-4 bg-orange-50 rounded-lg border border-orange-100">
                <div className="text-2xl font-bold text-orange-600">
                  ₹
                  {averageTransaction.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                  })}
                </div>
                <div className="text-sm text-orange-600 font-medium">
                  Average Transaction
                </div>
              </div>
              <div className="text-center p-4 bg-purple-50 rounded-lg border border-purple-100">
                <div className="text-2xl font-bold text-purple-600">
                  {(weeklyStats.weeklyCount / 7 || 0).toFixed(1)}
                </div>
                <div className="text-sm text-purple-600 font-medium">
                  Daily Average
                </div>
              </div>
              <div className="text-center p-4 bg-indigo-50 rounded-lg border border-indigo-100">
                <div className="text-2xl font-bold text-indigo-600">
                  {monthlyStats.monthlyCount > 0
                    ? (
                        (monthlyStats.monthlyCredits +
                          monthlyStats.monthlyDebits) /
                        monthlyStats.monthlyCount
                      ).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                      })
                    : "0.00"}
                </div>
                <div className="text-sm text-indigo-600 font-medium">
                  Monthly Avg (₹)
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdditionalStats;
